/**
 * Shared React Query key factory, fetch funkcije i hook-ovi za obligation/search API.
 * Batch fetch za više akcionih planova u jedan keširani query — izbegava N+1 pattern.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ObligationService from '../../../shared/services/ObligationService';
import { invalidateAndRefetchProfileObligations } from '@/features/user/profile/profileObligationsQuery';

/** Query key factory za obligation keš. */
export const obligationKeys = {
  all: ['obligation'],
  search: (filters) => [...obligationKeys.all, 'search', filters],
  drafts: (filters = {}) => [...obligationKeys.all, 'drafts', filters],
  forActionPlan: (actionPlanId, obligationId) => [
    ...obligationKeys.all,
    'actionPlan',
    actionPlanId,
    obligationId ? `obligation-${obligationId}` : null
  ].filter(Boolean),
  /** Jedan query za više AP ID-jeva — jedan keš entry, batch fetch (Promise.all). */
  forActionPlans: (actionPlanIds) => [
    ...obligationKeys.all,
    'actionPlans',
    [...(actionPlanIds || [])].filter(Boolean).sort((a, b) => String(a).localeCompare(String(b))).join(','),
  ],
};

const STALE_TIME_MS = 60 * 1000;

/**
 * Fetch obaveza za jedan akcioni plan.
 *
 * @param {string|number} actionPlanId - ID akcionog plana.
 * @param {object} [options]
 * @param {AbortSignal} [options.signal] - Signal za otkazivanje zahteva.
 * @param {string|number} [options.obligationId] - Opcioni filter po ID obaveze.
 * @returns {Promise<{ obligations: object[], actionPlanId: string|number }>}
 */
export async function fetchObligationsForActionPlan(actionPlanId, options = {}) {
  if (!actionPlanId) return { obligations: [] };
  const { signal, ...restOpts } = options;
  const searchParams = { action_plan_id: actionPlanId };
  if (restOpts?.obligationId) {
    searchParams.id = restOpts.obligationId;
  }
  const response = await ObligationService.search(searchParams, signal);
  const obligations = Array.isArray(response?.obligations) ? response.obligations : [];
  return { obligations, actionPlanId };
}

/**
 * React Query hook za obaveze jednog akcionog plana. Keš po actionPlanId i opcionom obligationId.
 *
 * @param {string|number} actionPlanId - ID akcionog plana.
 * @param {import('@tanstack/react-query').UseQueryOptions & { obligationId?: string|number }} [options] - Dodatne opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult<{ obligations: object[], actionPlanId: string|number }>}
 */
export function useObligationsForActionPlanQuery(actionPlanId, options = {}) {
  const obligationId = options?.obligationId;
  return useQuery({
    queryKey: obligationKeys.forActionPlan(actionPlanId, obligationId),
    queryFn: ({ signal }) =>
      fetchObligationsForActionPlan(actionPlanId, { ...options, signal }),
    enabled: !!actionPlanId,
    staleTime: STALE_TIME_MS,
    ...options,
  });
}

/**
 * Batch fetch obaveza za više akcionih planova. Jedan query key za skup ID-jeva —
 * N paralelnih mrežnih poziva u queryFn, deljeni keš između potrošača (Home draft + implementation AP-ovi).
 *
 * @param {(string|number)[]} actionPlanIds - Niz ID-jeva akcionih planova.
 * @param {object} [options]
 * @param {AbortSignal} [options.signal] - Signal za otkazivanje zahteva.
 * @returns {Promise<{ obligationsByAp: Record<string|number, object[]>, allObligations: object[] }>}
 */
export async function fetchObligationsForActionPlans(actionPlanIds, options = {}) {
  const { signal } = options;
  const ids = [...(actionPlanIds || [])].filter(Boolean);
  if (ids.length === 0) return { obligationsByAp: {}, allObligations: [] };
  const results = await Promise.all(
    ids.map(async (id) => {
      const { obligations } = await fetchObligationsForActionPlan(id, { signal });
      return { actionPlanId: id, obligations };
    })
  );
  const obligationsByAp = {};
  const allObligations = [];
  results.forEach(({ actionPlanId, obligations }) => {
    obligationsByAp[actionPlanId] = obligations;
    allObligations.push(...obligations.map((o) => ({ ...o, action_plan_id: actionPlanId })));
  });
  return { obligationsByAp, allObligations };
}

/**
 * React Query hook za batch fetch obaveza više akcionih planova.
 *
 * @param {(string|number)[]} actionPlanIds - Niz ID-jeva akcionih planova.
 * @param {import('@tanstack/react-query').UseQueryOptions} [options] - Dodatne React Query opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useObligationsForActionPlansQuery(actionPlanIds, options = {}) {
  const ids = [...(actionPlanIds || [])].filter(Boolean);
  return useQuery({
    queryKey: obligationKeys.forActionPlans(ids),
    queryFn: ({ signal }) => fetchObligationsForActionPlans(ids, { signal }),
    enabled: ids.length > 0,
    staleTime: STALE_TIME_MS,
    ...options,
  });
}

/**
 * Vraća QueryClient instancu (helper za cache operacije u obligation modulu).
 *
 * @returns {import('@tanstack/react-query').QueryClient}
 */
export function useObligationQueryClient() {
  return useQueryClient();
}

/**
 * Invalidira obligation keš nakon mutation-a (po AP i globalno, uključujući profilni tab).
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient - React Query klijent.
 * @param {string|number} [actionPlanId] - ID akcionog plana za ciljanu invalidaciju.
 * @returns {void}
 */
function invalidateObligationCaches(queryClient, actionPlanId) {
  if (actionPlanId) {
    queryClient.invalidateQueries({
      queryKey: obligationKeys.forActionPlan(actionPlanId),
      refetchType: 'all',
    });
    queryClient.invalidateQueries({
      queryKey: obligationKeys.forActionPlans([actionPlanId]),
      refetchType: 'all',
    });
  }
  queryClient.invalidateQueries({ queryKey: obligationKeys.all });
  void invalidateAndRefetchProfileObligations(queryClient);
}

/**
 * Mutation za čuvanje ili ažuriranje obaveze. Invalidira obligation query-je za akcioni plan na uspeh.
 *
 * @param {string|number} [actionPlanId] - ID akcionog plana za cache invalidaciju.
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useSaveObligationMutation(actionPlanId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => ObligationService.saveOrUpdate(payload),
    onSuccess: (_, variables) => {
      invalidateObligationCaches(queryClient, variables?.action_plan_id ?? actionPlanId);
    },
  });
}

/**
 * Fetch nacrta obaveza (draft/search).
 *
 * @param {object} [filters] - Filteri za pretragu nacrta.
 * @param {object} [options]
 * @param {AbortSignal} [options.signal] - Signal za otkazivanje zahteva.
 * @returns {Promise<{ obligations: object[], filters: object }>}
 */
export async function fetchObligationDrafts(filters = {}, options = {}) {
  const { signal } = options;
  const response = await ObligationService.searchDrafts(filters, signal);
  const obligations = Array.isArray(response?.obligations) ? response.obligations : [];
  return { obligations, filters };
}

/**
 * React Query hook za listu nacrta obaveza.
 *
 * @param {object} [filters] - Filteri za pretragu nacrta.
 * @param {import('@tanstack/react-query').UseQueryOptions} [options] - Dodatne React Query opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useObligationDraftsQuery(filters = {}, options = {}) {
  return useQuery({
    queryKey: obligationKeys.drafts(filters),
    queryFn: ({ signal }) => fetchObligationDrafts(filters, { signal }),
    staleTime: STALE_TIME_MS,
    ...options,
  });
}

/**
 * Mutation za čuvanje nacrta obaveze. Invalidira obligation i draft query-je na uspeh.
 *
 * @param {string|number} [actionPlanId] - ID akcionog plana za cache invalidaciju.
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useSaveObligationDraftMutation(actionPlanId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => ObligationService.saveOrUpdateDraft(payload),
    onSuccess: (_, variables) => {
      invalidateObligationCaches(queryClient, variables?.action_plan_id ?? actionPlanId);
      queryClient.invalidateQueries({ queryKey: obligationKeys.drafts() });
    },
  });
}

/**
 * Mutation za slanje nacrta obaveze na odobrenje (draft/submit).
 *
 * @param {string|number} [actionPlanId] - ID akcionog plana za cache invalidaciju.
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useSubmitObligationDraftMutation(actionPlanId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (obligationId) => ObligationService.submitDraft(obligationId),
    onSuccess: () => {
      invalidateObligationCaches(queryClient, actionPlanId);
      queryClient.invalidateQueries({ queryKey: obligationKeys.drafts() });
    },
  });
}
